import {Injectable} from '@angular/core';

interface ExternalFeedbackSession {
  instanceId: string;
  token: string;
}

@Injectable({providedIn: 'root'})
export class ExternalFeedbackSessionService {
  private readonly storageKey: string = 'external-feedback-session';
  private currentSession: ExternalFeedbackSession | null = null;

  public set(instanceId: string, token: string): void {
    this.currentSession = {instanceId, token};
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.currentSession));
    } catch {
      // The invitation can still be used in this tab when storage is unavailable.
    }
  }

  public get(instanceId: string): string | null {
    const session = this.currentSession ?? this.readStoredSession();
    return session?.instanceId === instanceId && session.token.trim() !== '' ? session.token : null;
  }

  public clear(instanceId: string): void {
    if (this.currentSession?.instanceId === instanceId) {
      this.currentSession = null;
    }

    try {
      if (this.readStoredSession()?.instanceId === instanceId) {
        sessionStorage.removeItem(this.storageKey);
      }
    } catch {
      // Storage may be unavailable during cleanup.
    }
  }

  private readStoredSession(): ExternalFeedbackSession | null {
    try {
      const raw = sessionStorage.getItem(this.storageKey);
      if (raw === null) {
        return null;
      }

      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null || !('instanceId' in parsed) || !('token' in parsed)) {
        return null;
      }

      return typeof parsed.instanceId === 'string' && typeof parsed.token === 'string'
        ? {instanceId: parsed.instanceId, token: parsed.token}
        : null;
    } catch {
      return null;
    }
  }
}
